import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { getUserToken } from 'src/shared/utils/session'
import queryString from 'query-string'
import _ from 'lodash'

export const checkoutApi = createApi({
  reducerPath: 'checkoutApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v2/storefront/checkout',
    prepareHeaders: (headers) => {
      const token = getUserToken()
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers
    }
  }),
  tagTypes: ['Orders', 'Order'],
  endpoints: (builder) => ({
    getPaymentMethods: builder.query({
      query: ({order_token}) => ({
        url: '/payment_methods',
        method: 'GET',
        headers: {
          'X-Spree-Order-Token': order_token,
        }
      })
    }),
    createPayment: builder.mutation({
      query: ({ order_token, payment_data, custom_headers = {} }) => ({
        url: '/create_payment',
        method: 'POST',
        headers: {
          'X-Spree-Order-Token': order_token,
          ...custom_headers
        },
        body: payment_data
      })
    }),
    completeOrder: builder.mutation({
      query: ({order_token}) => ({
        url: '/complete',
        method: 'PATCH',
        headers: {
          'X-Spree-Order-Token': order_token,
        }
      })
    }),
    updateOrder: builder.mutation({
      query: ({order_token, order_data}) => ({
        url: '/?include=billing_address,shipping_address',
        method: 'PATCH',
        headers: {
          'X-Spree-Order-Token': order_token,
        },
        body: order_data
      }),
      transformResponse: (response, meta, arg) => {
        const { data, included } = response;

        const order_data = data?.attributes || {};
        const relationships = data?.relationships || {};

        const billing_address = relationships?.billing_address?.data || null;
        const shipping_address = relationships?.shipping_address?.data || null;

        let parsed_billing_address = null;
        let parsed_shipping_address = null;

        if(included && included.length > 0 && billing_address) {
          const found_billing_address = included.find((item) => item.id === billing_address.id && item.type === 'address');
          if (found_billing_address) {
            parsed_billing_address = _.cloneDeep(found_billing_address.attributes);
            parsed_billing_address.id = found_billing_address.id;
          }
        }

        if(included && included.length > 0 && shipping_address) {
          const found_shipping_address = included.find((item) => item.id === shipping_address.id && item.type === 'address');
          if (found_shipping_address) {
            parsed_shipping_address = _.cloneDeep(found_shipping_address.attributes);
            parsed_shipping_address.id = found_shipping_address.id;
          }
        }

        return {
          id: data.id,
          type: data.type,
          ...order_data,
          billing_address: parsed_billing_address,
          shipping_address: parsed_shipping_address,
        }
      }
    }),
    advanceCheckout: builder.mutation({
      query: ({order_token}) => ({
        url: '/advance',
        method: 'PATCH',
        headers: {
          'X-Spree-Order-Token': order_token,
        }
      })
    }),
    advanceCheckoutWithAdditionalData: builder.mutation({
      query: ({order_token}) => ({
        url: '/advance?include=line_items,billing_address,shipping_address,promotions',
        method: 'PATCH',
        headers: {
          'X-Spree-Order-Token': order_token,
        }
      }),
      transformResponse: (response, meta, arg) => {
        const { data, included } = response;

        const order_data = data?.attributes || {};
        const relationships = data?.relationships || {};

        const line_items = relationships?.line_items?.data || [];
        const billing_address = relationships?.billing_address?.data || null;
        const shipping_address = relationships?.shipping_address?.data || null;
        const promotions = relationships?.promotions?.data || [];

        const parsed_line_items = [];
        if (included && included.length > 0 && line_items.length > 0) {
          line_items.forEach((li) => {
            const found_line_item = included.find((item) => item.id === li.id && item.type === 'line_item');
            if(found_line_item){
              const li_data = _.cloneDeep(found_line_item.attributes);
              li_data.id = found_line_item.id;
              li_data.variant = _.cloneDeep(found_line_item.relationships?.variant?.data || {});
              parsed_line_items.push(li_data);
            } else {
              parsed_line_items.push(_.cloneDeep(li));
            }
          })
        } else {
          parsed_line_items.push(...line_items.map((li) => _.cloneDeep(li)));
        }

        let parsed_billing_address = billing_address;
        if (included && included.length > 0 && billing_address) {
          const found_billing_address = included.find((item) => item.id === billing_address.id && item.type === 'address');
          if (found_billing_address) {
            parsed_billing_address = _.cloneDeep(found_billing_address.attributes);
            parsed_billing_address.id = found_billing_address.id;
          }
        }

        let parsed_shipping_address = shipping_address;
        if (included && included.length > 0 && shipping_address) {
          const found_shipping_address = included.find((item) => item.id === shipping_address.id && item.type === 'address');
          if (found_shipping_address) {
            parsed_shipping_address = _.cloneDeep(found_shipping_address.attributes);
            parsed_shipping_address.id = found_shipping_address.id;
          }
        }

        const parsed_promotions = [];
        if (included && included.length > 0 && promotions.length > 0) {
          promotions.forEach((promo) => {
            const found_promo = included.find((item) => item.id === promo.id && item.type === 'promotion');
            if (found_promo) {
              const promo_data = _.cloneDeep(found_promo.attributes);
              promo_data.id = found_promo.id;
              parsed_promotions.push(promo_data);
            } else {
              parsed_promotions.push(_.cloneDeep(promo));
            }
          });
        }else{
          parsed_promotions.push(...promotions.map((promo) => _.cloneDeep(promo)));
        }

        return {
          id: data.id,
          type: data.type,
          ...order_data,
          line_items: parsed_line_items,
          billing_address: parsed_billing_address,
          shipping_address: parsed_shipping_address,
          promotions: parsed_promotions,
        };
      }
    })
  })
})

export const {
  useGetPaymentMethodsQuery,
  useAdvanceCheckoutMutation,
  useAdvanceCheckoutWithAdditionalDataMutation,
  useCreatePaymentMutation,
  useCompleteOrderMutation,
  useUpdateOrderMutation,
} = checkoutApi